import { generateMnemonic, mnemonicToSeedSync, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
import { HDKey } from '@scure/bip32';
import { secp256k1 } from '@noble/curves/secp256k1';
import { keccak_256 } from '@noble/hashes/sha3';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';
import { base58check } from '@scure/base';
import { rpc } from './ws';
import { csGet, csSet, csDiag, csRemove, hasCloud } from './cloud-storage';
import { generateKeyB64, encryptSeed, decryptSeed, isEncrypted } from './crypto';
import { getSeedKey } from './me';

// Seed ciphertext keys in CloudStorage, scoped by Telegram user id so a shared
// device never leaks one account's seed to another. Telegram CloudStorage keys
// accept only [A-Za-z0-9_-] (a ':' is silently rejected — its callback never
// fires, so the write is lost) — the id is joined with an underscore.
//
// Two kinds of wallet:
//  - "account" (legacy): addresses at m/44'/195'/0'/0/{derivationIndex} from the
//    one shared seed in SEED_KEY, keyed by the user-level SeedKey. Never
//    re-minted — existing addresses and funds depend on it.
//  - "independent": every wallet created or imported now has its own seed in
//    `WALLET_SEED_KEY_{seedRef}`, keyed by that wallet's own server key.
const SEED_KEY = 'asia_seed_v1';
const WALLET_SEED_KEY = 'asia_wseed_v1';
// Account xpub cached by older builds to derive legacy HD addresses. No longer
// written; only removed on reset.
const XPUB_KEY = 'asia_xpub_v1';
const tgId = () => window.Telegram?.WebApp?.initDataUnsafe?.user?.id ?? null;
const scopedKey = (base: string) => {
  const id = tgId();
  return id == null ? base : `${base}_${id}`;
};
const seedStorageKey = () => scopedKey(SEED_KEY);
const xpubStorageKey = () => scopedKey(XPUB_KEY);
const walletSeedStorageKey = (seedRef: string) => scopedKey(`${WALLET_SEED_KEY}_${seedRef}`);

const b58check = base58check(sha256);
const TRON_PREFIX = 0x41;
const ACCOUNT_PATH = "m/44'/195'/0'/0";

/** Fresh 24-word BIP-39 mnemonic (256-bit entropy, matches the app's seed UI). */
export function createMnemonic(): string {
  return generateMnemonic(wordlist, 256);
}

export function isValidMnemonic(mnemonic: string): boolean {
  return validateMnemonic(mnemonic, wordlist);
}

/** TRON base58 ('T…') address + public key for a derived (public) HD node.
 *  secp256k1 pubkey → keccak → 0x41‖last20 → base58check. */
function tronFromNode(node: { publicKey: Uint8Array | null }): { address: string; publicKey: string } {
  if (!node.publicKey) throw new Error('tron derivation failed');
  const pub = secp256k1.ProjectivePoint.fromHex(node.publicKey).toRawBytes(false); // 0x04‖X‖Y
  const addr20 = keccak_256(pub.slice(1)).slice(-20);
  const payload = new Uint8Array(21);
  payload[0] = TRON_PREFIX;
  payload.set(addr20, 1);
  return { address: b58check.encode(payload), publicKey: bytesToHex(node.publicKey) };
}

const accountNode = (mnemonic: string) =>
  HDKey.fromMasterSeed(mnemonicToSeedSync(mnemonic)).derive(ACCOUNT_PATH);

/** Derive the TRON address at `index` from the mnemonic (m/44'/195'/0'/0/index). */
export function deriveTron(mnemonic: string, index = 0): { address: string; publicKey: string } {
  return tronFromNode(accountNode(mnemonic).deriveChild(index));
}

/** Random CloudStorage slot name for an independent wallet's ciphertext. */
function newSeedRef(): string {
  return bytesToHex(crypto.getRandomValues(new Uint8Array(16)));
}

/** The legacy shared account mnemonic — throws a tagged Error naming the step
 *  that failed so callers can surface an actionable message. */
export async function getSeedOrThrow(token: string, reauthToken?: string): Promise<string> {
  const stored = await csGet(seedStorageKey());
  if (!stored) throw new Error('no_ciphertext');
  if (!isEncrypted(stored)) throw new Error('bad_ciphertext');
  let key: string;
  try {
    ({ key } = await getSeedKey(token, reauthToken));
  } catch (e) {
    throw new Error(`seed_key:${e instanceof Error ? e.message : 'unknown'}`);
  }
  try {
    return await decryptSeed(stored, key);
  } catch {
    throw new Error('decrypt_failed');
  }
}

/** Diagnostic string for the legacy seed ciphertext location (dev troubleshooting). */
export const seedDiag = () => csDiag(seedStorageKey());

/** Which seed a wallet uses — enough to find and decrypt it. */
export interface WalletSeedRef {
  id: string;
  seedKind?: SeedKind;
  seedRef?: string | null;
  derivationIndex?: number;
}

async function getIndependentSeed(wallet: WalletSeedRef, reauthToken?: string): Promise<string> {
  if (!wallet.seedRef) throw new Error('no_seed_ref');
  const stored = await csGet(walletSeedStorageKey(wallet.seedRef));
  if (!stored) throw new Error('no_ciphertext');
  if (!isEncrypted(stored)) throw new Error('bad_ciphertext');
  let key: string;
  try {
    ({ key } = await rpc<{ key: string }>('wallets.getSeedKey', { id: wallet.id, reauthToken }));
  } catch (e) {
    throw new Error(`seed_key:${e instanceof Error ? e.message : 'unknown'}`);
  }
  try {
    return await decryptSeed(stored, key);
  } catch {
    throw new Error('decrypt_failed');
  }
}

/** The mnemonic + HD index that sign for this wallet. Independent wallets use
 *  their own seed at index 0; legacy wallets use the shared seed at their
 *  `derivationIndex`. Pass a reauth token when a PIN/biometric is set. */
export async function getWalletSeedOrThrow(
  token: string,
  wallet: WalletSeedRef,
  reauthToken?: string,
): Promise<{ mnemonic: string; index: number }> {
  if (wallet.seedKind === 'independent') {
    return { mnemonic: await getIndependentSeed(wallet, reauthToken), index: 0 };
  }
  return { mnemonic: await getSeedOrThrow(token, reauthToken), index: wallet.derivationIndex ?? 0 };
}

/** Wipe every seed ciphertext this account may hold on this device + cloud
 *  (wallet-reset flow). `wallets` supplies the independent slots to remove. */
export async function clearAllSeeds(wallets: WalletSeedRef[]): Promise<void> {
  await Promise.all([
    csRemove(seedStorageKey()),
    csRemove(xpubStorageKey()),
    ...wallets
      .filter((w) => w.seedKind === 'independent' && w.seedRef)
      .map((w) => csRemove(walletSeedStorageKey(w.seedRef!))),
  ]);
}

/** Remove one independent wallet's ciphertext (after the server deleted it). */
export async function clearWalletSeed(wallet: WalletSeedRef): Promise<void> {
  if (wallet.seedKind === 'independent' && wallet.seedRef) {
    await csRemove(walletSeedStorageKey(wallet.seedRef));
  }
}

export type SeedKind = 'account' | 'independent';

export interface RemoteWallet {
  id: string;
  name: string;
  address: string;
  derivationIndex: number;
  isPrimary: boolean;
  /** Missing from older API builds — treat as "account". */
  seedKind?: SeedKind;
  seedRef?: string | null;
  createdAt: string;
}

/** Create a wallet with its OWN seed: a fresh one, or `mnemonic` when importing.
 *  Order matters: the ciphertext must land durably in CloudStorage before the
 *  server registers the wallet (with its wrapped key) — otherwise a registered
 *  wallet could be unrecoverable. A failed registration removes only the new,
 *  never-referenced slot. */
export async function createIndependentWallet(
  name: string,
  opts: { mnemonic?: string; isPrimary?: boolean } = {},
): Promise<RemoteWallet> {
  const mnemonic = opts.mnemonic ?? createMnemonic();
  const { address, publicKey } = deriveTron(mnemonic, 0);
  const seedRef = newSeedRef();
  const slot = walletSeedStorageKey(seedRef);
  const seedKey = await generateKeyB64();
  const durable = await csSet(slot, await encryptSeed(mnemonic, seedKey));
  // Real Telegram: refuse to register a wallet whose seed isn't in the synced,
  // durable store. In dev/browser (no CloudStorage) the local mirror is accepted.
  if (!durable && hasCloud()) {
    await csRemove(slot);
    throw new Error('seed_store_failed');
  }
  try {
    const r = await rpc<{ wallet: RemoteWallet }>('wallets.create', {
      name,
      address,
      publicKey,
      derivationIndex: 0,
      isPrimary: opts.isPrimary ?? false,
      seedKind: 'independent',
      seedRef,
      seedKey,
    });
    return r.wallet;
  } catch (e) {
    await csRemove(slot);
    throw e;
  }
}

/** Create the default "Main wallet" on first launch — with its own seed.
 *  Idempotent: an account that already has a wallet is left untouched. */
export async function createDefaultWallet(_token: string): Promise<void> {
  const existing = await rpc<{ wallets: RemoteWallet[] }>('wallets.list');
  if (existing.wallets.length > 0) return;
  await createIndependentWallet('Main wallet', { isPrimary: true });
}

/** List the user's wallets from the server. */
export async function fetchWallets(_token: string): Promise<RemoteWallet[]> {
  const { wallets } = await rpc<{ wallets: RemoteWallet[] }>('wallets.list');
  return wallets;
}

export const renameWalletApi = (_token: string, id: string, name: string) =>
  rpc<{ ok: boolean }>('wallets.rename', { id, name });

export const deleteWalletApi = (_token: string, id: string) =>
  rpc<{ ok: boolean }>('wallets.remove', { id });
